/**
 * The backlight: a sun just behind the planet, lighting a thin crescent on its
 * limb. Seen from here only a thin sliver of the lit half shows: inside the
 * disc it is bounded by the terminator, a half-ellipse whose minor radius is
 * R·(1 − CRESCENT_W). The crescent and its atmosphere are shaded per pixel on
 * the GPU; the sun's glare and rays are drawn here.
 */
import { glow, spike } from './draw'
import { lightAt } from './light'
import type { Canvas2D, Light } from './renderer/renderer'
import type { Viewport } from './viewport'

const RAD = Math.PI / 180

/* =====================================================================
   TWEAKABLES
   ===================================================================== */
export const CRESCENT = 1.0 /* the lit crescent and its atmosphere: 0 = hidden, 1 = full */
export const SUN = 1.0 /* the sun peeking over the limb: 0 = hidden, 1 = full */
export const LIGHT_DEG =
  -28 /* where on the limb the light comes from (deg; 0 = right, -90 = top) */
const CRESCENT_W = 0.04 /* the crescent's thickness at its widest, as a fraction of the globe's radius */
export const CRESCENT_IN: readonly [number, number] = [
  2.6, 4.6,
] /* the backlight comes up (s after the burst) — it finishes while the globe glides to the hero */
export const SUN_IN: readonly [number, number] = [3.3, 5.0] /* ...and the sun */
const SUN_SIZE = 1.0 /* the sun's glare and rays */
export const DAY_DIM = 0.9 /* how much city lights fade where the sun has risen on them */
/* ===================================================================== */

/** The backlight at its home side, where the intro leaves it. */
export const HOME_LIGHT: Light = lightAt(LIGHT_DEG * RAD, CRESCENT_W)

/** The backlight coming from `angle` (rad). */
export function backlightAt(angle: number): Light {
  return lightAt(angle, CRESCENT_W)
}

/** The sun, at the limb of a globe of radius `R` centred on (`cx`, `cy`), shining with strength `k`. `tw` is the time, for the shimmer. */
export function drawSun(
  ctx: Canvas2D,
  vp: Viewport,
  light: Light,
  cx: number,
  cy: number,
  R: number,
  k: number,
  tw: number
) {
  if (k < 0.003) return
  const sx = cx + light.lx * R * 1.005
  const sy = cy + light.ly * R * 1.005
  const S = vp.min * SUN_SIZE
  ctx.globalCompositeOperation = 'lighter'
  glow(ctx, sx, sy, S * 0.3, 0.22 * k, 0.12, '255,214,170')
  glow(ctx, sx, sy, S * 0.09, 0.55 * k, 0.2, '255,236,210')
  glow(ctx, sx, sy, S * 0.025, 0.95 * k, 0.35, '255,252,246')
  /* rays: a long flat streak along the limb's tangent, and a short star */
  const tangent = Math.atan2(light.lx, -light.ly)
  const jitter = 1 + 0.03 * Math.sin(tw * 2.3)
  spike(ctx, sx, sy, tangent, S * 0.3 * jitter, S * 0.0035, '255,236,210', 0.4 * k)
  spike(ctx, sx, sy, tangent + Math.PI, S * 0.3 * jitter, S * 0.0035, '255,236,210', 0.4 * k)
  for (let i = 0; i < 6; i++) {
    const angle = tangent + Math.PI / 6 + i * (Math.PI / 3)
    const length = S * (i % 2 ? 0.07 : 0.12) * jitter
    spike(ctx, sx, sy, angle, length, S * 0.003, '255,244,230', 0.5 * k)
  }
}
